import { KeyboardEvent, useState } from "react";
import { isValidEmail } from "../../helper/common";
import styles from "./TenantInviteSection.module.css";

export type TenantInviteMethod = "email" | "self";

export type TenantInvite = {
  name: string;
  email: string;
  tenantMethod: TenantInviteMethod;
};

type TenantInviteSectionProps = {
  currentUserEmail?: string;
  invites: TenantInvite[];
  onChange: (invites: TenantInvite[]) => void;
  _t: (key: string, fallback?: string) => string;
  variant?: "application" | "join";
};

const TenantInviteSection = ({
  currentUserEmail,
  invites,
  onChange,
  _t,
  variant = "application",
}: TenantInviteSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState<TenantInviteMethod>("email");
  const [error, setError] = useState("");

  const resetForm = () => {
    setName("");
    setEmail("");
    setMethod("email");
    setError("");
    setIsOpen(false);
  };

  const addTenant = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedName) {
      setError(_t("nameRequired", "Full name is required"));
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError(_t("invalidEmail", "Enter a valid email address"));
      return;
    }
    if (trimmedEmail === currentUserEmail?.toLowerCase()) {
      setError(_t("cannotAddYourself", "You cannot add yourself"));
      return;
    }
    if (invites.some((invite) => invite.email === trimmedEmail)) {
      setError(_t("emailAlreadyAdded", "This email has already been added"));
      return;
    }
    if (invites.length >= 4) {
      setError(_t("maxParticipantsReached", "Maximum of 4 tenants allowed"));
      return;
    }

    onChange([
      ...invites,
      {
        name: trimmedName,
        email: trimmedEmail,
        tenantMethod: method,
      },
    ]);
    resetForm();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addTenant();
    }
  };

  return (
    <div
      className={`${styles.section} ${
        variant === "join" ? styles.joinVariant : ""
      }`}
    >
      {invites.map((invite) => (
        <div className={styles.addedPerson} key={invite.email}>
          <span className={styles.avatar}>{invite.name.charAt(0)}</span>
          <div className={styles.personDetails}>
            <strong>{invite.name}</strong>
            <p>
              {invite.email} · {_t("tenant", "Tenant")} ·{" "}
              {invite.tenantMethod === "self"
                ? _t("illFillItIn", "I'll fill it in")
                : _t("inviteByEmail", "Send email")}
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              onChange(invites.filter(({ email }) => email !== invite.email))
            }
            aria-label={_t("removeTenant", "Remove tenant")}
          >
            ×
          </button>
        </div>
      ))}

      {isOpen ? (
        <div className={styles.form}>
          <div className={styles.formHeader}>
            <span>{_t("tenant", "Tenant")}</span>
          </div>
          <input
            value={name}
            autoFocus
            onChange={(event) => {
              setName(event.target.value);
              setError("");
            }}
            onBeforeInput={(event) => {
              const data = (event.nativeEvent as InputEvent).data;
              if (data && !/^[A-Za-zÀ-ÿ\s'-]+$/.test(data)) {
                event.preventDefault();
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={_t("fullName", "Full name")}
            aria-label={_t("fullName", "Full name")}
          />
          <input
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value.toLowerCase());
              setError("");
            }}
            onKeyDown={handleKeyDown}
            placeholder="email@example.com"
            aria-label="Email"
          />
          {/* <select
            className={styles.method}
            value={method}
            onChange={(event) =>
              setMethod(event.target.value as TenantInviteMethod)
            }
            aria-label={_t("tenantAddMethod", "Tenant add method")}
          >
            <option value="email">{_t("inviteByEmail", "Invite by email")}</option>
            <option value="self">{_t("illFillItIn", "I'll fill it in")}</option>
          </select> */}
          <button
            className={styles.addButton}
            type="button"
            onClick={addTenant}
            disabled={!name.trim() || !email.trim()}
          >
            {_t("add", "Add")}
          </button>
          <button
            className={styles.closeButton}
            type="button"
            onClick={resetForm}
            aria-label={_t("remove", "Remove")}
          >
            <img src="/images/delete-icon.svg" alt="" />
          </button>
          {error && <p className={styles.error}>{error}</p>}
        </div>
      ) : (
        <button
          className={styles.openButton}
          type="button"
          onClick={() => setIsOpen(true)}
          disabled={invites.length >= 4}
        >
          <span className={styles.addIcon}>+</span>
          <strong>{_t("addTenant", "Add tenant")}</strong>
        </button>
      )}
    </div>
  );
};

export default TenantInviteSection;
